import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button, Checkbox, Dropdown } from "@app-components";
import {
  migrateCardMigrationBatch,
  useBuilding,
  useCardMigrationFormats,
  type CardMigrationOptions,
  type CardMigrationRowInput,
  type CardMigrationRowOutcome,
} from "@/api";
import { useWelcomeSuperAccess } from "@/hooks/useWelcomeSuperAccess";
import { useSnackbarStore } from "@/store/zustandStore";
import { NotificationType } from "@/types";
import PageBackButton from "@/components/PageBackButton";
import CardMigrationImporter from "@/components/super_settings/card_migration/CardMigrationImporter";
import CardMigrationResults from "@/components/super_settings/card_migration/CardMigrationResults";
import ConfirmCardMigrationModal from "@/components/super_settings/card_migration/ConfirmCardMigrationModal";
import { countByStatus } from "@/components/super_settings/card_migration/cardMigrationLabels";

export const Route = createFileRoute(
  "/_portal/building/supersettings/card-migration",
)({
  component: RouteComponent,
});

// Small enough that one request stays well within the api_more_memory timeout.
const BATCH_SIZE = 50;

const cardStyle = {
  padding: "var(--wlcm-spacing-sm)",
  background: "var(--wlcm-color-inactive-grey)",
  borderRadius: "var(--wlcm-border-radius-sm)",
  display: "flex",
  flexDirection: "column" as const,
  gap: "var(--wlcm-spacing-xs)",
};

type Upload = { rows: CardMigrationRowInput[]; fileName: string };
type Run = {
  dryRun: boolean;
  rows: CardMigrationRowOutcome[];
  isRunning: boolean;
};

const toBatches = <T,>(items: T[]) =>
  Array.from({ length: Math.ceil(items.length / BATCH_SIZE) }, (_, i) =>
    items.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE),
  );

function RouteComponent() {
  const isWelcomeWorkdaysUser = useWelcomeSuperAccess();
  const { data: building } = useBuilding();
  const { addNotification } = useSnackbarStore();
  const { data: formats } = useCardMigrationFormats();

  const [isImporterOpen, setIsImporterOpen] = useState(false);
  const [upload, setUpload] = useState<Upload | null>(null);
  const [formatKey, setFormatKey] = useState<string>();
  const [allowExistingCards, setAllowExistingCards] = useState(false);
  const [run, setRun] = useState<Run | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const isRunning = !!run?.isRunning;
  const formatOptions = useMemo(
    () => (formats ?? []).map((f) => ({ label: f.label, value: f.key })),
    [formats],
  );
  const formatLabel = formats?.find((f) => f.key === formatKey)?.label ?? "";
  // A finished dry run of the current file and options says how many will migrate.
  const dryRunCounts =
    run && run.dryRun && !run.isRunning ? countByStatus(run.rows) : undefined;

  useEffect(() => {
    if (!isRunning) return;
    // Leaving stops the remaining batches; warn first.
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isRunning]);

  if (!isWelcomeWorkdaysUser) return null;

  const resetRun = () => setRun(null);

  const start = async (dryRun: boolean) => {
    if (!upload || !formatKey) return;
    setIsConfirmOpen(false);
    const options: CardMigrationOptions = {
      formatKey,
      allowExistingCards,
      dryRun,
    };
    const outcomes: CardMigrationRowOutcome[] = [];
    setRun({ dryRun, rows: [], isRunning: true });

    try {
      for (const batch of toBatches(upload.rows)) {
        outcomes.push(...(await migrateCardMigrationBatch(options, batch)));
        setRun({ dryRun, rows: [...outcomes], isRunning: true });
      }
    } catch (error) {
      console.error("Card migration batch error:", error);
      addNotification(
        `Stopped after ${outcomes.length} of ${upload.rows.length} rows. Running the file again is safe.`,
        NotificationType.ERROR,
      );
    } finally {
      setRun({ dryRun, rows: [...outcomes], isRunning: false });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--wlcm-spacing-md)",
      }}
    >
      <div>
        <div className="backAndBreadcrumbsContainer">
          <PageBackButton fallbackPath={{ to: "/building/supersettings" }} />
        </div>
        <h1 style={{ margin: 0 }}>Card migration</h1>
        <p style={{ color: "var(--wlcm-color-text-secondary)" }}>
          Moves existing plastic cards onto users' Sharry profiles in{" "}
          <strong>{building.displayName}</strong> and activates them. Start with
          a dry run: it checks every row without changing anything.
        </p>
      </div>

      <section style={cardStyle} aria-labelledby="card-migration-setup">
        <h2 id="card-migration-setup">Setup</h2>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <Button
            icon="upload_file"
            onClick={() => setIsImporterOpen(true)}
            disabled={isRunning}
          >
            {upload ? "Upload another file" : "Upload CSV or Excel"}
          </Button>
          {upload && (
            <span>
              <strong>{upload.rows.length}</strong> rows from {upload.fileName}
            </span>
          )}
        </div>
        <Dropdown
          compact
          label="Card format"
          options={formatOptions}
          value={formatKey}
          onChange={(option) => {
            setFormatKey(option.value as string);
            resetRun();
          }}
          required
          disabled={isRunning}
        />
        <Checkbox
          label="Allow cards that already exist in ARX (ARX buildings only)"
          checked={allowExistingCards}
          onChange={(checked) => {
            setAllowExistingCards(checked);
            resetRun();
          }}
          disabled={isRunning}
        />
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          <Button
            onClick={() => start(true)}
            disabled={!upload || !formatKey || isRunning}
            isLoading={isRunning && run?.dryRun}
          >
            Run dry run
          </Button>
          <Button
            danger
            onClick={() => setIsConfirmOpen(true)}
            disabled={!upload || !formatKey || isRunning}
            isLoading={isRunning && !run?.dryRun}
          >
            Migrate cards
          </Button>
        </div>
      </section>

      {run && upload && (
        <section style={cardStyle} aria-label="Results">
          <CardMigrationResults
            title={run.dryRun ? "Dry run" : "Migration"}
            rows={run.rows}
            totalRows={upload.rows.length}
            isRunning={run.isRunning}
            reportFileName={`card-migration-${building.displayName}-${run.dryRun ? "dry-run" : "migration"}`}
          />
        </section>
      )}

      <CardMigrationImporter
        open={isImporterOpen}
        onClose={() => setIsImporterOpen(false)}
        onSubmit={(rows, fileName) => {
          setUpload({ rows, fileName });
          resetRun();
          setIsImporterOpen(false);
        }}
      />

      <ConfirmCardMigrationModal
        open={isConfirmOpen}
        buildingName={building.displayName}
        formatLabel={formatLabel}
        rowCount={upload?.rows.length ?? 0}
        wouldMigrate={dryRunCounts?.would_migrate}
        onConfirm={() => start(false)}
        onClose={() => setIsConfirmOpen(false)}
      />
    </div>
  );
}
